import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Container, Stack, Tab, Tabs, TextField } from '@mui/material';

import { CruisesContract } from '@luxuryescapes/contract-svc-cruise';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import ImagesForm from '~/components/Common/Forms/ImagesForm';
import Spinner from '~/components/Common/Spinner';
import ShipAmenities from '~/components/Cruises/pages/Ships/ShipAmenities';
import { CruiseShip } from '~/components/Cruises/pages/Ships/types';

import shipService from '~/services/cruises/ShipService';

interface image {
  id: string | number;
  publicImageId: string;
  order: number;
  title?: string;
  hidden?: boolean;
  filename?: string;
}

const ShipImageEditor = () => {
  const [isReady, setIsReady] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [images, setImages] = useState<Array<image>>([]);
  const [ship, setShip] = useState<CruiseShip>(null);
  const [cruiseCriticId, setCruiseCriticId] = useState<number>(null);

  const { shipId, shipName, tab } = useParams<{
    shipId: string;
    shipName: string;
    tab: string;
  }>();

  const fetchCruiseDetails = async () => {
    const [shipResponse, shipImagesResponse] = await Promise.all([
      shipService.getShipById(shipId),
      shipService.getShipImages(shipId),
    ]);

    const { result: ship } = shipResponse;
    const { result: images } = shipImagesResponse;

    setShip(ship);
    setCruiseCriticId(ship.cruiseCriticId);
    setImages(
      images
        .map((image) => ({
          ...image,
          id: image.idImage,
          publicImageId: image.idCloudinaryExternal,
        }))
        .sort((a, b) => a.order - b.order),
    );
    setIsReady(true);
  };

  const onEditShip = async () => {
    if (!cruiseCriticId) return;

    setIsFetching(true);
    await shipService.updateShip(shipId, cruiseCriticId, ship.isCrawlerAmenitiesCurated);

    setIsFetching(false);
  };

  const onAddImage = async (cloudinaryId: string, filename?: string) => {
    const newImage: CruisesContract.ShipImage = await shipService.createShipImage(shipId, {
      idCloudinaryExternal: cloudinaryId,
      filename: filename ? filename : undefined,
    });

    newImage.id = newImage.idImage;
    return newImage;
  };

  const onUpdateImages = (newImageList) => {
    const updatePayload = newImageList
      .filter((image) => image.idCloudinaryExternal)
      .map((image) => ({
        idImage: image.id,
        idCloudinaryExternal: image.idCloudinaryExternal,
        order: image.order,
      }));

    if (!updatePayload.length) return Promise.resolve();
    return shipService.updateShipImages(shipId, updatePayload);
  };

  const onDeleteImage = (imageId) => {
    return shipService.deleteShipImage(shipId, imageId);
  };

  useEffect(() => {
    fetchCruiseDetails();
  }, []);

  const handleEditableCruiseCriticId = useCallback((e) => {
    setCruiseCriticId(Number(e.target.value));
  }, []);

  return (
    <MUIProvider>
      <Container maxWidth="xl">
        <Helmet>
          <title>Ships | {shipName || shipId}</title>
        </Helmet>

        <PageHeader title="Edit Ship" backButton />

        {!isReady && <Spinner />}

        {isReady && (
          <Box>
            <form>
              <PageSubheader title="Ship overview" />

              <Stack spacing={2} direction="column">
                <Stack spacing={2} direction="row">
                  <TextField fullWidth disabled label="Ship Name" value={ship.name} InputProps={{ readOnly: true }} />
                  <TextField
                    type="number"
                    fullWidth
                    disabled={isFetching}
                    label="Cruise Critic ID"
                    value={cruiseCriticId}
                    defaultValue={ship.cruiseCriticId}
                    onChange={handleEditableCruiseCriticId}
                  />
                </Stack>

                <Box>
                  <Button variant="contained" disabled={isFetching} onClick={onEditShip}>
                    Save Changes
                  </Button>
                </Box>
              </Stack>
            </form>

            <Box mt={3} border={1} borderColor="divider" borderRadius={1}>
              <Tabs value={tab} variant="scrollable">
                <Tab value="images" label="Images" to={`/cruises/ships/${shipId}/images`} component={Link} />
                <Tab
                  value="amenities"
                  label="Crawled Amenities"
                  to={`/cruises/ships/${shipId}/amenities`}
                  component={Link}
                />
              </Tabs>

              <Box borderTop={1} borderColor="divider">
                {tab === 'images' && (
                  <Box p={2}>
                    <ImagesForm
                      images={images}
                      onAddImage={onAddImage}
                      onUpdateImages={onUpdateImages}
                      onDeleteImage={onDeleteImage}
                    />
                  </Box>
                )}

                {tab === 'amenities' && <ShipAmenities ship={ship} onUpdateShip={fetchCruiseDetails} />}
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </MUIProvider>
  );
};

export default ShipImageEditor;
